interface ProfileAvatarProps {
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const ProfileAvatar = ({ name, size = 'md', className = '' }: ProfileAvatarProps) => {
  const getInitials = (fullName: string) => {
    // Remove quotes and other special characters, then split on spaces
    const cleanName = fullName.replace(/['"]/g, '');
    const names = cleanName.split(' ').filter(word => word.length > 0);
    
    if (names.length >= 2) {
      // Take first letter of first name and last name (skip nicknames in middle)
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    } else if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-12 h-12 text-sm';
      case 'md':
        return 'w-20 h-20 text-lg';
      case 'lg':
        return 'w-32 h-32 text-2xl';
      case 'xl':
        return 'w-48 h-48 text-4xl';
      default:
        return 'w-20 h-20 text-lg';
    }
  };

  const getColorFromName = (name: string) => {
    // Generate a consistent color based on the name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generate colors that work well for avatars
    const colors = [
      'bg-gradient-to-br from-ocean-teal to-ocean-teal-dark',
      'bg-gradient-to-br from-purple-600 to-purple-800',
      'bg-gradient-to-br from-red-600 to-red-800',
      'bg-gradient-to-br from-green-600 to-green-800',
      'bg-gradient-to-br from-blue-600 to-blue-800',
      'bg-gradient-to-br from-indigo-600 to-indigo-800',
      'bg-gradient-to-br from-orange-600 to-orange-800',
      'bg-gradient-to-br from-teal-600 to-teal-800',
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  const initials = getInitials(name);
  const sizeClasses = getSizeClasses();
  const colorClasses = getColorFromName(name);

  return (
    <div 
      className={`${sizeClasses} ${colorClasses} rounded-full flex items-center justify-center text-white font-bold shadow-lg ${className}`}
    >
      {initials}
    </div>
  );
};

export default ProfileAvatar;